/**
 * Lens write-target resolution — pure function consumed by the compose
 * bar to decide what affordance to show and which message ids to send
 * as `metadata.parent_ids` on the next message.
 *
 * Pure module (no React, no api imports) so it can be unit-tested in
 * Jest without pulling the Vite `import.meta` chain. Same separation
 * pattern as useActiveSquadsReducer.ts.
 */
import type { LensState } from './LensContext';
import type { CursorTarget } from './replyCursor';
import { residentNamesCopy } from './trailResidents';


export type WriteTargetAffordance = 'plain' | 'pill' | 'picker' | 'cursor-pill' | 'fork-pill';

export interface PickerOption {
  personaId: string;
  personaName: string;
  latestTurnId: string;
  /** The target persona's trail (from their latest envelope) — see trailId below. */
  trailId?: string | null;
}

export interface ResolveWriteTargetResult {
  parentIds: string[];
  affordance: WriteTargetAffordance;
  pillCopy?: string;
  pickerOptions?: PickerOption[];
  /**
   * 2026-06-12 (field 303f4b3a): when the target rides a known trail, the
   * send must stamp `trail_id` and OMIT parent_ids — a trail send is legal
   * at the DC1-lite gate, while non-tip parent_ids are rejected (the pill
   * promised "Replying on X's trail" but the wire sent doomed parent_ids).
   * null = no trail known (pre-SP1 envelopes) → parent_ids path unchanged.
   */
  trailId?: string | null;
  /** Reply-targeting §3 — transport. 'messages' = existing POST /messages. */
  sendVia: 'messages' | 'spine_reply' | 'fork';
  /** spine_root_message_id when sendVia === 'spine_reply' or 'fork'. */
  spineId?: string;
  /** §3 — fork mode: the mid-spine message M to branch from (cursor's cursorTurnId). */
  forkFromMessageId?: string;
}


/**
 * `messagesByLens` is a map keyed by "lens key" — caller computes the
 * keys ahead of time so this helper stays pure:
 *   - canopy: any key → all combined
 *   - single: `persona:<personaId>`
 *   - multi: one key per active persona
 *   - trail: `trail:<trailId>`
 */
export type MessagesByLens = Record<
  string,
  {
    messageId: string;
    personaId?: string;
    /** metadata.trail_id from the envelope (null/absent on pre-SP1 messages). */
    trailId?: string | null;
    /** metadata.persona_display_name_at_join — operator copy must not show raw uuids. */
    personaName?: string;
  }[]
>;


export function resolveWriteTarget(
  lens: LensState,
  messagesByLens: MessagesByLens,
  cursorTarget?: CursorTarget | null,
  routePersonaName?: string,
): ResolveWriteTargetResult {
  // Reply-targeting §3 — the cursor outranks lens inference (the TOP row of
  // master-spec §9.2's resolution table). No cursor → today's behavior,
  // byte-identical.
  if (cursorTarget) {
    // §3 fork — cursorMode 'fork' on a COMPANION spine branches a new spine from M.
    // Main-spine forking is out of v1 (different transport) → fall through to reply.
    if (lens.cursorMode === 'fork'
        && !cursorTarget.isMainSpine && cursorTarget.spineRootMessageId) {
      const who = residentNamesCopy(cursorTarget.residentNames ?? [])
        ?? cursorTarget.residentName ?? 'companion';
      return {
        parentIds: [],
        affordance: 'fork-pill',
        pillCopy: `New path from ${who} · here`,
        trailId: cursorTarget.trailId,
        sendVia: 'fork',
        spineId: cursorTarget.spineRootMessageId,
        forkFromMessageId: cursorTarget.cursorTurnId,
      };
    }
    if (!cursorTarget.isMainSpine && cursorTarget.spineRootMessageId) {
      // P2: ONE formatter (residentNamesCopy) names every trail resident —
      // a shared trail answers with all of them.
      const who = residentNamesCopy(cursorTarget.residentNames ?? [])
        ?? cursorTarget.residentName ?? 'companion';
      return {
        parentIds: [],                       // backend authors parents server-side
        affordance: 'cursor-pill',
        pillCopy: `Replying to ${who}`,
        trailId: cursorTarget.trailId,
        sendVia: 'spine_reply',
        spineId: cursorTarget.spineRootMessageId,
      };
    }
    return {
      parentIds: [],                          // main spine: linear default
      affordance: 'cursor-pill',
      pillCopy: `Replying to ${routePersonaName ?? 'the trail'}`,
      trailId: cursorTarget.trailId,
      sendVia: 'messages',
    };
  }

  switch (lens.mode) {
    case 'canopy': {
      // Latest authored message across every lens bucket. Tiebreak by
      // message-id lexicographic order (a reasonable proxy when message
      // ids embed timestamps; bucket-insertion order would be fragile).
      const all = Object.values(messagesByLens).flat();
      if (all.length === 0) {
        return { parentIds: [], affordance: 'plain', sendVia: 'messages' };
      }
      const latest = all.reduce(
        (best, cur) => (cur.messageId > best.messageId ? cur : best),
        all[0],
      );
      return {
        parentIds: [latest.messageId],
        affordance: 'plain',
        sendVia: 'messages',
      };
    }

    case 'single': {
      const personaId = Array.from(lens.activePersonaIds)[0];
      if (!personaId) {
        return { parentIds: [], affordance: 'plain', sendVia: 'messages' };
      }
      const msgs = messagesByLens[`persona:${personaId}`] || [];
      const latest = msgs[msgs.length - 1];
      return {
        parentIds: latest ? [latest.messageId] : [],
        affordance: 'pill',
        // Display name when the envelope carries one; raw id only as a
        // last resort ([[feedback_2_digit_iq_glance_lens]] — no uuids in copy).
        pillCopy: `Replying on ${latest?.personaName || personaId}'s trail`,
        trailId: latest?.trailId ?? null,
        sendVia: 'messages',
      };
    }

    case 'trail': {
      if (!lens.activeTrailId) {
        return { parentIds: [], affordance: 'plain', sendVia: 'messages' };
      }
      const msgs = messagesByLens[`trail:${lens.activeTrailId}`] || [];
      const latest = msgs[msgs.length - 1];
      return {
        parentIds: latest ? [latest.messageId] : [],
        affordance: 'pill',
        pillCopy: `Replying in Trail ${lens.activeTrailId}`,
        trailId: lens.activeTrailId,
        sendVia: 'messages',
      };
    }

    case 'multi': {
      const personas = Array.from(lens.activePersonaIds);
      const pickerOptions: PickerOption[] = personas.map((pid) => {
        const msgs = messagesByLens[`persona:${pid}`] || [];
        const latest = msgs[msgs.length - 1];
        return {
          personaId: pid,
          personaName: latest?.personaName || pid,
          latestTurnId: latest?.messageId || '',
          trailId: latest?.trailId ?? null,
        };
      });

      // Sticky: prefer lastMultiTarget if it's still in the active set;
      // else pick the persona whose latest authored turn is most recent.
      const stickyOk = lens.lastMultiTarget
        && personas.includes(lens.lastMultiTarget);
      const target = stickyOk
        ? lens.lastMultiTarget!
        : _pickLastAuthoredTerminal(pickerOptions);

      const targetMsgs = target ? (messagesByLens[`persona:${target}`] || []) : [];
      const latest = targetMsgs[targetMsgs.length - 1];

      return {
        parentIds: latest ? [latest.messageId] : [],
        affordance: 'picker',
        pickerOptions,
        trailId: latest?.trailId ?? null,
        sendVia: 'messages',
      };
    }
  }
}


/**
 * Tiebreak when no sticky multi-target is set: pick the persona whose
 * latest message-id sorts last (rough proxy for "most recently authored"
 * when message ids embed timestamps).
 */
function _pickLastAuthoredTerminal(opts: PickerOption[]): string {
  if (opts.length === 0) return '';
  return opts.reduce(
    (best, cur) => (cur.latestTurnId > best.latestTurnId ? cur : best),
    opts[0],
  ).personaId;
}

/**
 * P3 SP1 — per-send metadata from the lens. Trail mode stamps trail_id and
 * OMITS parent_ids: _validate_parent_ids_dc1_lite (chat.py) accepts only the
 * global tip, and a pinned trail's tip usually isn't it. Parentage on a
 * pinned trail rides with full DC1 (L2 G5). Returns undefined when empty
 * (preserves the legacy no-metadata send).
 */
export function buildSendMetadata(
  parentIds: string[],
  activeTrailId: string | null,
): Record<string, unknown> | undefined {
  if (activeTrailId) return { trail_id: activeTrailId };
  return parentIds.length > 0 ? { parent_ids: parentIds } : undefined;
}
